import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useRouteContext } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { deliveryControls } from '@bb/contracts';
import { Button, ErrorState, ReadOnlyBanner } from '@bb/ui';
import { ApiError, type PutRateLimitsBody, type RateLimitsView } from '../../api/client.ts';
import { usePermissions } from '../access/usePermissions.ts';
import { CHANNEL_LABELS } from '../campaigns/labels.ts';

const CHANNELS = deliveryControls.RATE_LIMIT_CHANNELS;
const MAX_PER_MINUTE = 1_000_000;
const INVALID = `Enter a whole number from 1 to ${MAX_PER_MINUTE.toLocaleString('en-US')}, or leave it empty for no limit.`;
const errorText = (error: unknown) =>
  error instanceof ApiError ? (error.problem.issues as Array<{ path: string; message: string }> | undefined)?.map((i) => `${i.path}: ${i.message}`).join('; ') || error.message : 'Something went wrong.';

type Draft = Record<string, string>;
const toDraft = (view: RateLimitsView): Draft => Object.fromEntries(CHANNELS.map((c) => [c, String(view.limits.find((l) => l.channel === c)?.perMinute ?? '')]));

/** The limits to save, or the channels whose value is not a limit. */
function checkDraft(draft: Draft): { body: PutRateLimitsBody | null; issues: Record<string, string> } {
  const limits: PutRateLimitsBody['limits'] = [];
  const issues: Record<string, string> = {};
  for (const channel of CHANNELS) {
    const text = draft[channel]?.trim() ?? '';
    if (!text) continue;
    const perMinute = Number(text);
    if (Number.isInteger(perMinute) && perMinute >= 1 && perMinute <= MAX_PER_MINUTE) limits.push({ channel, perMinute });
    else issues[channel] = INVALID;
  }
  return Object.keys(issues).length ? { body: null, issues } : { body: { limits }, issues };
}

/**
 * Settings → Messaging rate limits (BB-102, CMP-007): the most sends per minute all campaigns and Canvas of the
 * environment make on a channel. A campaign may set its own rate too; the lower one applies. Empty means no limit.
 */
export function RateLimitsPage() {
  const { environmentId } = useParams({ from: '/env/$environmentId/settings/rate-limits' });
  const { api } = useRouteContext({ from: '/env/$environmentId/settings/rate-limits' });
  const queryClient = useQueryClient();
  const canEdit = usePermissions().has('env.manage');
  const key = ['rate-limits', environmentId];
  const view = useQuery({ queryKey: key, queryFn: () => api.rateLimits(environmentId) });
  const [draft, setDraft] = useState<Draft | null>(null);
  const save = useMutation({
    mutationFn: (body: PutRateLimitsBody) => api.putRateLimits(environmentId, body),
    onSuccess: (next) => {
      queryClient.setQueryData(key, next);
      setDraft(null);
    },
  });

  if (view.isError) return <ErrorState title="Rate limits could not be loaded" message={errorText(view.error)} onRetry={() => void view.refetch()} />;
  if (!view.data) return <p aria-busy="true">Loading…</p>;
  const values = draft ?? toDraft(view.data);
  const { body, issues } = checkDraft(values);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (draft && body) save.mutate(body);
  };
  return (
    <section>
      <h1>Messaging rate limits</h1>
      <p className="hint">
        The most messages per minute all campaigns and Canvas of this environment send on a channel. A campaign may set a lower rate of its own; the lower one applies. Sends over the limit wait for the next minute, and are dropped
        after the campaign's maximum delay (72 hours unless it sets less). Transactional messages are not limited.
      </p>
      {!canEdit && <ReadOnlyBanner>Rate limits are changed by admins (env.manage).</ReadOnlyBanner>}
      <form aria-label="Rate limits" onSubmit={submit}>
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Channel</th>
              <th scope="col">Sends per minute</th>
            </tr>
          </thead>
          <tbody>
            {CHANNELS.map((channel) => (
              <tr key={channel}>
                <td>{CHANNEL_LABELS[channel]}</td>
                <td>
                  {canEdit ? (
                    <>
                      <input
                        type="number"
                        min={1}
                        max={MAX_PER_MINUTE}
                        step={1}
                        placeholder="No limit"
                        aria-label={`${CHANNEL_LABELS[channel]}, sends per minute`}
                        aria-invalid={issues[channel] ? true : undefined}
                        value={values[channel] ?? ''}
                        onChange={(e) => setDraft({ ...values, [channel]: e.target.value })}
                      />
                      {issues[channel] && (
                        <p className="bb-field__error" role="alert">
                          {issues[channel]}
                        </p>
                      )}
                    </>
                  ) : values[channel] ? (
                    Number(values[channel]).toLocaleString('en-US')
                  ) : (
                    <span className="hint">No limit</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {canEdit && (
          <Button type="submit" variant="primary" loading={save.isPending} disabled={!draft || !body}>
            Save limits
          </Button>
        )}
        {save.isError && <p role="alert">{errorText(save.error)}</p>}
        {save.isSuccess && !draft && <p role="status">Saved.</p>}
      </form>
    </section>
  );
}
